import {Modal, Input, Button, Select, Form} from "antd";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {useFormik} from "formik";
import stores from "../../../store/stores";
import {useFetch} from "../../../hooks/useFetch";
import turkishToEnglish from "constants/turkishToEnglish";
import {arrayAlphabeticalSort} from "utils/helpers";
import {toTurkishProperCase} from "utils/toTurkishProperCase";

export const CreateZoneModal = ({visibility, onCancel, setRecallZone}) => {
    const [loading, setLoading] = useState(false);
    const [selectedCityId, setSelectedCityId] = useState(null);
    const {t} = useTranslation(["common"]);

    // Zone Management listesi
    const [zoneManagementLoading, zoneManagementData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.operationOrganizationStore.getZoneManagementList.bind(null, {
            skip: 0,
            limit: 1000,
            paginate: false
        }),
        dependencies: [visibility]
    });

    // City listesi
    const [cityLoading, cityData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.optionStore.getCityList.bind(null, {
            limit: 1000,
            sort: "name"
        }),
        dependencies: [visibility]
    });

    // District listesi (selected city'ye göre)
    const [districtLoading, districtData] = useFetch({
        defaultDataValue: {},
        fetchFn: () => {
            if (!selectedCityId) {
                return Promise.resolve({data: []});
            }
            return stores.optionStore.getDistrictList({
                cityId: selectedCityId,
                limit: 1000,
                sort: "name"
            });
        },
        dependencies: [selectedCityId]
    });

    // Saha Yöneticisi listesi
    const [fieldManagerLoading, fieldManagerData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.operationOrganizationStore.getFieldManagerList.bind(null, {
            skip: 0,
            limit: 1000
        }),
        dependencies: [visibility]
    });

    // İK listesi
    const [ikLoading, ikData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.operationOrganizationStore.getIkList.bind(null, {
            skip: 0,
            limit: 1000
        }),
        dependencies: [visibility]
    });

    // Acente listesi
    const [agencyLoading, agencyData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.operationOrganizationStore.getAgencyList.bind(null, {
            skip: 0,
            limit: 1000
        }),
        dependencies: [visibility]
    });

    // Acente-İK listesi
    const [agencyIkLoading, agencyIkData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.operationOrganizationStore.getAgencyIkList.bind(null, {
            skip: 0,
            limit: 1000
        }),
        dependencies: [visibility]
    });

    const formik = useFormik({
        initialValues: {
            name: "",
            zoneManagementId: null,
            cityId: null,
            districtIds: [],
            syUserIds: [],
            ikUserIds: [],
            agencyUserIds: [],
            agencyIkUserIds: []
        },
        validateOnMount: true,
        validate: values => {
            const errors = {};

            if (!values.name) {
                errors.name = "Bölge adı zorunludur!";
            } else if (values.name.length < 2) {
                errors.name = "Bölge adı en az 2 karakter olmalıdır!";
            }

            if (!values.zoneManagementId) {
                errors.zoneManagementId = "Bölge müdürlüğü seçimi zorunludur!";
            }

            if (!values.cityId) {
                errors.cityId = "Şehir seçimi zorunludur!";
            }

            return errors;
        },
        onSubmit: async values => {
            setLoading(true);
            try {
                const result = await stores.operationOrganizationStore.createZone({
                    name: values.name,
                    zoneManagementId: values.zoneManagementId,
                    cityId: values.cityId,
                    districtIds: values.districtIds,
                    syUserIds: values.syUserIds,
                    ikUserIds: values.ikUserIds,
                    agencyUserIds: values.agencyUserIds,
                    agencyIkUserIds: values.agencyIkUserIds
                });

                if (result) {
                    setRecallZone(prev => !prev);
                    formik.resetForm();
                    setSelectedCityId(null);
                    onCancel();
                }
            } catch (error) {
                console.error("Form submission failed:", error);
            } finally {
                setLoading(false);
            }
        }
    });

    const handleCancel = () => {
        formik.resetForm();
        setSelectedCityId(null);
        onCancel();
    };

    const handleCityChange = value => {
        setSelectedCityId(value);
        formik.setFieldValue("cityId", value);
        formik.setFieldValue("districtIds", []); // City değiştiğinde district'leri temizle
    };

    if (!visibility) {
        return null;
    }

    const filterOption = (input, option) => {
        if (!input || !option || !option.children) return false;

        const searchInput = turkishToEnglish(input).toLowerCase();
        const englishInput = turkishToEnglish(searchInput).toLowerCase();

        const optionText = turkishToEnglish(option.children).toLowerCase();
        const englishOptionText = turkishToEnglish(optionText).toLowerCase();

        return optionText.includes(searchInput) || englishOptionText.includes(englishInput);
    };

    return (
        <Modal
            title="Yeni Bölge Oluştur"
            visible={true}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    İptal
                </Button>,
                <Button key="submit" type="primary" loading={loading} onClick={formik.handleSubmit}>
                    Oluştur
                </Button>
            ]}
            width={600}
            destroyOnClose={true}
        >
            <form onSubmit={formik.handleSubmit} style={{marginTop: 20}}>
                <div style={{marginBottom: 16}}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: 8,
                            fontSize: "14px",
                            fontWeight: 500
                        }}
                    >
                        Bölge Adı *
                    </label>
                    <Input
                        name="name"
                        placeholder="Bölge adını giriniz (ör: İstanbul Avrupa 1)"
                        maxLength={100}
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        status={formik.touched.name && formik.errors.name ? "error" : ""}
                    />
                    {formik.touched.name && formik.errors.name && (
                        <div style={{color: "#ff4d4f", fontSize: "12px", marginTop: 4}}>
                            {formik.errors.name}
                        </div>
                    )}
                </div>

                <div style={{marginBottom: 16}}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: 8,
                            fontSize: "14px",
                            fontWeight: 500
                        }}
                    >
                        Bölge Müdürlüğü *
                    </label>
                    <Select
                        placeholder="Bölge müdürlüğü seçiniz"
                        style={{width: "100%"}}
                        value={formik.values.zoneManagementId}
                        onChange={value => formik.setFieldValue("zoneManagementId", value)}
                        loading={zoneManagementLoading}
                        status={
                            formik.touched.zoneManagementId && formik.errors.zoneManagementId
                                ? "error"
                                : ""
                        }
                        showSearch
                        filterOption={filterOption}
                    >
                        {Array.isArray(zoneManagementData?.data) &&
                            arrayAlphabeticalSort(zoneManagementData?.data || [], "name").map(
                                item => (
                                    <Select.Option key={item._id} value={item._id}>
                                        {item.name}
                                    </Select.Option>
                                )
                            )}
                    </Select>
                    {formik.touched.zoneManagementId && formik.errors.zoneManagementId && (
                        <div style={{color: "#ff4d4f", fontSize: "12px", marginTop: 4}}>
                            {formik.errors.zoneManagementId}
                        </div>
                    )}
                </div>
                {formik.values.zoneManagementId && (
                    <div style={{marginBottom: 16}}>
                        <label
                            style={{
                                display: "block",
                                marginBottom: 8,
                                fontSize: "14px",
                                fontWeight: 500
                            }}
                        >
                            Bölge Müdürü
                        </label>
                        <span style={{fontSize: "14px"}}>
                            {zoneManagementData?.data?.find(
                                item =>
                                    item?._id?.toString() ===
                                    formik?.values?.zoneManagementId?.toString()
                            )?.bmUser?.fullName || "-"}
                        </span>
                    </div>
                )}
                <div style={{marginBottom: 16}}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: 8,
                            fontSize: "14px",
                            fontWeight: 500
                        }}
                    >
                        Şehir *
                    </label>
                    <Select
                        placeholder="Şehir seçiniz"
                        style={{width: "100%"}}
                        value={formik.values.cityId}
                        onChange={handleCityChange}
                        loading={cityLoading}
                        status={formik.touched.cityId && formik.errors.cityId ? "error" : ""}
                        showSearch
                        filterOption={filterOption}
                    >
                        {Array.isArray(cityData?.data) &&
                            arrayAlphabeticalSort(cityData?.data || [], "name").map(item => (
                                <Select.Option key={item._id} value={item._id}>
                                    {toTurkishProperCase(item?.name)}
                                </Select.Option>
                            ))}
                    </Select>
                    {formik.touched.cityId && formik.errors.cityId && (
                        <div style={{color: "#ff4d4f", fontSize: "12px", marginTop: 4}}>
                            {formik.errors.cityId}
                        </div>
                    )}
                </div>

                <div style={{marginBottom: 16}}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: 8,
                            fontSize: "14px",
                            fontWeight: 500
                        }}
                    >
                        İlçeler
                    </label>
                    <Select
                        mode="multiple"
                        placeholder="İlçeleri seçiniz"
                        style={{width: "100%"}}
                        value={formik.values.districtIds}
                        onChange={value => formik.setFieldValue("districtIds", value)}
                        loading={districtLoading}
                        disabled={!selectedCityId}
                        filterOption={filterOption}
                    >
                        {Array.isArray(districtData?.data) &&
                            arrayAlphabeticalSort(districtData?.data || [], "name").map(item => (
                                <Select.Option key={item._id} value={item._id}>
                                    {toTurkishProperCase(item?.name)}
                                </Select.Option>
                            ))}
                    </Select>
                    <div style={{color: "#666", fontSize: "12px", marginTop: 4}}>
                        Önce şehir seçiniz, sonra ilçeleri seçebilirsiniz
                    </div>
                </div>

                <div style={{marginBottom: 16}}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: 8,
                            fontSize: "14px",
                            fontWeight: 500
                        }}
                    >
                        Saha Yöneticisi
                    </label>
                    <Select
                        placeholder="Saha yöneticisi seçiniz (opsiyonel)"
                        style={{width: "100%"}}
                        value={formik.values.syUserIds}
                        onChange={value => formik.setFieldValue("syUserIds", value)}
                        loading={fieldManagerLoading}
                        allowClear
                        showSearch
                        mode="multiple"
                        filterOption={filterOption}
                    >
                        {Array.isArray(fieldManagerData?.rows) &&
                            arrayAlphabeticalSort(fieldManagerData?.rows || [], "fullName").map(
                                item => (
                                    <Select.Option key={item._id} value={item._id}>
                                        {item.fullName}
                                    </Select.Option>
                                )
                            )}
                    </Select>
                    <div style={{color: "#666", fontSize: "12px", marginTop: 4}}>
                        Bir bölgeye yalnız bir saha yöneticisi atanabilir
                    </div>
                </div>
                <div style={{marginBottom: 16}}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: 8,
                            fontSize: "14px",
                            fontWeight: 500
                        }}
                    >
                        İK
                    </label>
                    <Select
                        placeholder="İK seçiniz (opsiyonel)"
                        style={{width: "100%"}}
                        value={formik.values.ikUserIds}
                        onChange={value => formik.setFieldValue("ikUserIds", value)}
                        loading={ikLoading}
                        allowClear
                        showSearch
                        mode="multiple"
                        filterOption={filterOption}
                    >
                        {Array.isArray(ikData?.rows) &&
                            arrayAlphabeticalSort(ikData?.rows || [], "fullName").map(item => (
                                <Select.Option key={item._id} value={item._id}>
                                    {item.fullName}
                                </Select.Option>
                            ))}
                    </Select>
                    <div style={{color: "#666", fontSize: "12px", marginTop: 4}}>
                        Bir bölgeye yalnız bir İK atanabilir
                    </div>
                </div>
                <div style={{marginBottom: 16}}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: 8,
                            fontSize: "14px",
                            fontWeight: 500
                        }}
                    >
                        Acente
                    </label>
                    <Select
                        placeholder="Acente seçiniz (opsiyonel)"
                        style={{width: "100%"}}
                        value={formik.values.agencyUserIds}
                        onChange={value => formik.setFieldValue("agencyUserIds", value)}
                        loading={agencyLoading}
                        allowClear
                        showSearch
                        mode="multiple"
                        filterOption={filterOption}
                    >
                        {Array.isArray(agencyData?.rows) &&
                            arrayAlphabeticalSort(agencyData?.rows || [], "fullName").map(item => (
                                <Select.Option key={item._id} value={item._id}>
                                    {item.fullName}
                                </Select.Option>
                            ))}
                    </Select>
                </div>
                <div style={{marginBottom: 16}}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: 8,
                            fontSize: "14px",
                            fontWeight: 500
                        }}
                    >
                        Acente-İK
                    </label>
                    <Select
                        placeholder="Acente-İK seçiniz (opsiyonel)"
                        style={{width: "100%"}}
                        value={formik.values.agencyIkUserIds}
                        onChange={value => formik.setFieldValue("agencyIkUserIds", value)}
                        loading={agencyIkLoading}
                        allowClear
                        showSearch
                        mode="multiple"
                        filterOption={filterOption}
                    >
                        {Array.isArray(agencyIkData?.rows) &&
                            arrayAlphabeticalSort(agencyIkData?.rows || [], "fullName").map(
                                item => (
                                    <Select.Option key={item._id} value={item._id}>
                                        {item.fullName}
                                    </Select.Option>
                                )
                            )}
                    </Select>
                </div>
            </form>
        </Modal>
    );
};
